// WhatsApp Utility Functions

/**
 * Extract and normalize phone number with + prefix
 * @param {string} from - Phone number string
 * @returns {string} Normalized phone number or 'Unknown'
 */
export const extractPhoneNumber = (from) => {
  if (!from) return 'Unknown';
  const match = from.match(/(\+?\d{10,15})/);
  return match ? match[1] : from;
};

/**
 * Capitalize name properly (Title Case)
 * @param {string} name - Name to capitalize
 * @returns {string} Capitalized name
 */
export const capitalizeName = (name) => {
  if (!name || name === 'Unknown') return name;
  return name.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Unified timestamp handler for various Firestore formats
 * @param {any} timestamp - Timestamp in various formats
 * @returns {number} Unix timestamp in milliseconds
 */
export const getUnifiedTimestamp = (timestamp) => {
  if (!timestamp) return Date.now();

  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate().getTime();
  }

  if (timestamp instanceof Date) {
    return timestamp.getTime();
  }

  if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    const date = new Date(timestamp);
    if (!isNaN(date.getTime())) {
      return date.getTime();
    }
  }

  if (timestamp && typeof timestamp === 'object') {
    if ('_seconds' in timestamp) {
      return (timestamp._seconds * 1000) + (timestamp._nanoseconds || 0) / 1000000;
    } else if ('seconds' in timestamp) {
      return (timestamp.seconds * 1000) + (timestamp.nanoseconds || 0) / 1000000;
    }
  }

  return Date.now();
};

/**
 * Extract message information from payload
 * @param {object} payload - Message payload
 * @param {string} direction - 'incoming' or 'outgoing'
 * @returns {object} Extracted message info
 */
export const extractMessageInfo = (payload) => {
  if (!payload) return { name: 'Unknown', phone: 'Unknown', body: '', type: 'text' };

  const phone = extractPhoneNumber(payload.From || payload.from);
  const name = payload.ProfileName || phone || 'Unknown';

  let body = '';
  if (payload.Body) {
    body = payload.Body;
  } else if (payload.text) {
    body = payload.text;
  } else if (payload.message?.text) {
    body = payload.message.text;
  } else if (payload.message?.body) {
    body = payload.message.body;
  } else if (payload.ButtonText) {
    body = `🔘 ${payload.ButtonText}`;
  } else if (payload.ButtonPayload) {
    body = `🔘 Button: ${payload.ButtonPayload}`;
  }

  let type = payload.MessageType || payload.message?.type || 'text';
  if (payload.ButtonText || payload.ButtonPayload) {
    type = 'button';
  }

  // Check for interactive button replies
  if (payload.interactive && payload.interactive.type === 'button_reply') {
    type = 'button';
    body = payload.interactive.button_reply.title || `Button: ${payload.interactive.button_reply.id}`;
  }

  // Check for interactive list replies
  if (payload.interactive && payload.interactive.type === 'list_reply') {
    type = 'interactive';
    body = payload.interactive.list_reply.title || `List: ${payload.interactive.list_reply.id}`;
  }

  return { name, phone, body, type };
};

/**
 * Get message type display text
 * @param {string} type - Message type
 * @returns {string} Display text
 */
export const getMessageTypeDisplay = (type) => {
  switch (type) {
    case 'button': return 'Button';
    case 'image': return 'Image';
    case 'document': return 'Document';
    case 'text':
    default: return 'Text';
  }
};

/**
 * Get initials from name
 * @param {string} name - Full name
 * @returns {string} Initials (max 2 characters)
 */
export const groupMessagesByPhone = (messageList) => {
  const grouped = {};

  messageList.forEach(msg => {
    const messageInfo = extractMessageInfo(msg.payload);
    const phone = messageInfo.phone;

    if (!grouped[phone]) {
      grouped[phone] = {
        phone: phone,
        name: messageInfo.name,
        messages: [],
        lastMessage: null,
        lastTimestamp: null
      };
    }

    grouped[phone].messages.push(msg);

    const msgTime = msg.timestamp?.toDate ? msg.timestamp.toDate() : new Date(msg.timestamp);
    if (!grouped[phone].lastTimestamp || msgTime > grouped[phone].lastTimestamp) {
      grouped[phone].lastMessage = msg;
      grouped[phone].lastTimestamp = msgTime;
    }
  });

  return grouped;
};
